import moment from "moment";
import _ from "lodash";
import { getDistributorName, getCountryName} from "./utils";

const MovieEntryProjectionExpression = "MovieEntryID, MovieID, LocalTitle, N_LocalTitle, ReleaseDate, N_ReleaseDate, Country, Distributor, N_Distributor, Reviewed, SourceUrl";
const MovieProjectionExpression = "MovieID, Poster, OriginalTitle, EnglishTitle, IsGruvi";

const updateItemDynamodb = (dynamodb, params) => {
  return new Promise( (resolve,reject) => {
    dynamodb.updateItem(params,(err, data) =>  {
      if (err){
        console.log(err, err.stack); // an error occurred
        reject(err);
      }else{
        resolve(data);
      }
    });
  });
  
}

const scanDynamodb = (dynamodb, params, startKey) => {
  return new Promise( (resolve,reject) => {

    if(startKey){
      params.ExclusiveStartKey = startKey;
    }

    dynamodb.scan(params, async (err, data) => {
      if(err){
        console.log(err, err.stack); // an error occurred
      }else{
        const items = data.Items;
        if(data.LastEvaluatedKey){
          scanDynamodb(dynamodb, params, data.LastEvaluatedKey).then( new_items => resolve([...new_items,...items]));
        }else{
          resolve(items);
        }
      }
    });

  });
}

const getBatchMovies = (dynamodb, ids) => {
  return new Promise( (resolve,reject) => {
    const next_ids = ids.splice(100);
    const params = {
      RequestItems: {
        Movie: {
          Keys: ids.map(o => ({MovieID: {S: o}}) ),
          ProjectionExpression: MovieProjectionExpression, 
        },
      }
    };
    dynamodb.batchGetItem(params, async (err, data) => {
      if (err) console.log(err, err.stack); // an error occurred
      else{
        const movies = data.Responses.Movie;
        if(data.UnprocessedKeys.length > 0){
          if(next_ids.length > 0){
            getBatchMovies(dynamodb, [...data.UnprocessedKeys, ...next_ids]).then(mvs => resolve([...mvs,...movies]))
          }else{
            getBatchMovies(dynamodb, data.UnprocessedKeys).then(mvs => resolve([...mvs,...movies]))
          }
        }else{
          if(next_ids.length > 0){
            getBatchMovies(dynamodb, next_ids).then(mvs => resolve([...mvs,...movies]))
          }else {
            resolve(movies);
          }
        }

      }
    });
  });
};

const getValidMovieEntries = (dynamodb, range) => {

  const params = {
    ExpressionAttributeValues: {
      ":ar": { BOOL: false},
      ":df": { S: range.from.format("YYYY-MM-DD")},
      ":dt": { S: range.to.format("YYYY-MM-DD")},
      ":mi": { S: ""},
    },
    FilterExpression: "(Archived = :ar or attribute_not_exists(Archived)) and ( (attribute_not_exists(N_ReleaseDate) and ReleaseDate BETWEEN :df and :dt) or N_ReleaseDate BETWEEN :df and :dt) and attribute_exists(MovieID) and MovieID <> :mi",
    ProjectionExpression: MovieEntryProjectionExpression, 
    TableName: "MovieEntry"
  };
  
  return scanDynamodb(dynamodb, params);
};

const getGruviMovies = (dynamodb, range) => {

  const params = {
    ExpressionAttributeValues: {
      ":gr": { BOOL: true }
    },
    FilterExpression: "IsGruvi = :gr",
    ProjectionExpression: MovieProjectionExpression, 
    TableName: "Movie"
  };
  
  return scanDynamodb(dynamodb, params);
};

const getEntriesForMovie = (dynamodb, movie_id) => {

  const params = {
    ExpressionAttributeValues: {
      ":ar": { BOOL: false},
      ":mid": { S: movie_id},
    },
    FilterExpression: "(Archived = :ar or attribute_not_exists(Archived)) and MovieID = :mid",
    ProjectionExpression: MovieEntryProjectionExpression, 
    TableName: "MovieEntry"
  };
  
  return scanDynamodb(dynamodb, params);
};

const getAllMovies = (dynamodb) => {

  const params = {
    ProjectionExpression: MovieProjectionExpression, 
    TableName: "Movie"
  };

  return scanDynamodb(dynamodb, params);
}



const formatMovieData = (movie, entries) => {
  if(entries){
    return { 
      movie_id: movie.MovieID.S,
      poster: movie.Poster.S,
      original_title: movie.OriginalTitle.S,
      english_title: movie.EnglishTitle.S,
      is_gruvi: movie.IsGruvi ? movie.IsGruvi.BOOL : false,
      movie_entries: entries.entries,
      next_release: entries.next_release,
      search_distributor: entries.search_distributor,
      search_country: entries.search_country,
      needs_review: entries.needs_review,
      is_public: entries.is_public,
    }

  }else{
    return { 
      movie_id: movie.MovieID.S,
      poster: movie.Poster.S,
      original_title: movie.OriginalTitle.S,
      english_title: movie.EnglishTitle.S,
      is_gruvi: movie.IsGruvi ? movie.IsGruvi.BOOL : false,
      movie_entries: [],
      next_release: null,
      search_distributor: null,
      search_country: null,
      needs_review: true,
      is_public: false,
    }
  }
}

const formatMovieEntryData = (entry) => {
  const formated_entry = {
    movie_id: entry.MovieID.S,
    entry_id: entry.MovieEntryID.S,
    country: entry.Country.S,
    local_title: entry.N_LocalTitle ? entry.N_LocalTitle.S : entry.LocalTitle.S,
    release_date: entry.N_ReleaseDate ? entry.N_ReleaseDate.S : entry.ReleaseDate.S,
    distributor: entry.N_Distributor ? entry.N_Distributor.S : entry.Distributor.S,
    reviewed: entry.Reviewed ? entry.Reviewed.BOOL : false,
    source_url: entry.SourceUrl ? entry.SourceUrl.S : "",
  }
  formated_entry.distributor = getDistributorName(formated_entry.distributor);
  return formated_entry;
} 

const groupMovieEntries = (items) => {

  return items.reduce( (acc,entry) => {
    const clean_entry = formatMovieEntryData(entry);
    const movie_id = clean_entry.movie_id;
    if(acc[movie_id]){

      const rdate = moment(clean_entry.release_date,"YYYY-MM-DD")
      acc[movie_id] = { 
        entries: [...acc[movie_id].entries, clean_entry],
        search_distributor: `${acc[movie_id].search_distributor},${clean_entry.distributor}`,
        search_country: `${acc[movie_id].search_country},${getCountryName(clean_entry.country)}`,
        next_release: rdate.isBefore(acc[movie_id].next_release) ? rdate : acc[movie_id].next_release,
        needs_review: acc[movie_id].needs_review || !clean_entry.reviewed,
        is_public: acc[movie_id].is_public || clean_entry.reviewed
      };
    }else{
      acc[movie_id] = {
        entries: [clean_entry],
        search_distributor: clean_entry.distributor,
        search_country: getCountryName(clean_entry.country),
        next_release: moment(clean_entry.release_date,"YYYY-MM-DD"),
        needs_review: !clean_entry.reviewed,
        is_public: clean_entry.reviewed
      };
    }
    return acc;
  },{});
}

const getAdminMoviesInfo = (dynamodb) => {
  return new Promise( (resolve,reject) => {
    getAllMovies(dynamodb).then(data => {

      const formated_data = data.reduce( (acc,movie) => {
        acc[movie.MovieID.S] = formatMovieData(movie);
        return acc;
      },{});

      resolve(formated_data);
    })
  });
}


const getGruviList = (dynamodb, range) => {
  return new Promise( (resolve,reject) => {

    let movie_entries = [];

    getGruviMovies(dynamodb, range).then(data => {
      console.log(data)
      let movies = data;
      let prm = movies.map( movie => {
        return getEntriesForMovie(dynamodb, movie.id);
      })

    }).then(data => {
      const formated_data = data.reduce( (acc,movie) => {
        const entries = movie_entries[movie.MovieID.S];
        acc[movie.MovieID.S] = formatMovieData(movie, entries);
        return acc;
      },{});
      console.log(formated_data)
      resolve(formated_data);
    });
  });
}

const getMovieList = (dynamodb, range) => {
  return new Promise( (resolve,reject) => {

    let movie_entries = [];

    getValidMovieEntries(dynamodb, range).then(data => {
      movie_entries = groupMovieEntries(data);

      return getBatchMovies(dynamodb, Object.keys(movie_entries));

    }).then(data => {
      const formated_data = data.reduce( (acc,movie) => {
        const entries = movie_entries[movie.MovieID.S];
        acc[movie.MovieID.S] = formatMovieData(movie, entries);
        return acc;
      },{});
      console.log(formated_data)
      resolve(formated_data);
    });
  });
}

const getDataForMovie = (dynamodb, movie_id) => {
  return new Promise( (resolve,reject) => {
    getEntriesForMovie(dynamodb, movie_id).then( data => {
      const entries = groupMovieEntries(data)[movie_id];
      getBatchMovies(dynamodb, [movie_id]).then( movies => {
        const movie = movies[0];
        resolve(formatMovieData(movie, entries));
      });
    });
  });
}

const createMovie = (dynamodb, original_title, english_title, poster ) => {
  return new Promise( (resolve,reject) => {
    const movie_id = `${_.chain(english_title).trim().lowerCase().replace(/\s/g,'').value()}-m`;
    const params = {
      ExpressionAttributeValues: {
        ":ot": {
          S: original_title
        }, 
        ":et": {
          S: english_title
        },
        ":p": {
          S: poster
        }
      }, 
      Key: {
        "MovieID": {
          S: movie_id
        } 
      }, 
      TableName: "Movie", 
      UpdateExpression: `SET OriginalTitle = :ot, EnglishTitle = :et, Poster = :p`
    };

    updateItemDynamodb(dynamodb, params).then( data => {
      const new_movie = { 
        movie_id,
        poster,
        original_title,
        english_title,
        is_gruvi: false,
        movie_entries: [],
        next_release: null,
        search_distributor: null,
        search_country: null,
        needs_review: true,
        is_public: false,
      };
      resolve(new_movie);

    }).catch(err => reject(err));
  });
}


const createMovieEntry = (dynamodb, movie_id, local_title, distributor, release_date, country ) => {

  return new Promise( (resolve,reject) => {

    const entry_id = `${_.chain(local_title).trim().lowerCase().replace(/\s/g,'').value()}-${country}-m`;

    const params = {
      ExpressionAttributeValues: {
        ":mid": {
          S: movie_id
        }, 
        ":t": {
          S: local_title
        }, 
        ":d": {
          S: release_date
        },
        ":c": {
          S: country
        },
        ":dis": {
          S: distributor
        },
        ":ar": {
          BOOL: false
        },
        ":rv": {
          BOOL: false
        },
      
      }, 
      Key: {
        "MovieEntryID": {
          S: entry_id
        } 
      }, 
      TableName: "MovieEntry", 
      UpdateExpression: `SET MovieID = :mid, LocalTitle = :t, ReleaseDate = :d, Distributor = :dis, Country = :c, Reviewed = :rv, Archived = :ar`
    };

    updateItemDynamodb(dynamodb, params).then( data => {
      const new_entry = {
        movie_id,
        entry_id,
        country,
        local_title,
        release_date,
        distributor,
        reviewed: false,
        source_url: "manual"
      }
      resolve(new_entry);
    }).catch(err => reject(err));
  
  });
};


const updateMovieEntry = (dynamodb, entry_id, movie_id, local_title, distributor, release_date, country, reviewed ) => {

  return new Promise( (resolve,reject) => {

    const params = {
      ExpressionAttributeValues: {
        ":mid": {
          S: movie_id
        }, 
        ":t": {
          S: local_title
        }, 
        ":d": {
          S: release_date
        },
        ":c": {
          S: country
        },
        ":dis": {
          S: distributor
        },
        ":rev":{
          BOOL: reviewed
        }
      }, 
      Key: {
        "MovieEntryID": {
          S: entry_id
        } 
      }, 
      TableName: "MovieEntry", 
      UpdateExpression: `SET MovieID = :mid, N_LocalTitle = :t, N_ReleaseDate = :d, N_Distributor = :dis, Country = :c, Reviewed = :rev`
    };

    updateItemDynamodb(dynamodb, params).then( data => {
      const update_entry = {
        movie_id,
        entry_id,
        country,
        local_title,
        release_date,
        distributor,
        reviewed: true,
      }
      resolve(update_entry);
    }).catch(err => reject(err));
  });
}

const updateMovie = (dynamodb, movie_id, original_title, english_title, poster, is_gruvi ) => {
  return new Promise( (resolve,reject) => {
    const params = {
      ExpressionAttributeValues: {
        ":ot": {
          S: original_title
        }, 
        ":et": {
          S: english_title
        },
        ":p": {
          S: poster
        },
        ":ig":{
          BOOL: is_gruvi
        }
      }, 
      Key: {
        "MovieID": {
          S: movie_id
        } 
      }, 
      TableName: "Movie", 
      UpdateExpression: `SET OriginalTitle = :ot, EnglishTitle = :et, Poster = :p, IsGruvi = :ig`
    };

    updateItemDynamodb(dynamodb, params).then( data => {
      const update_movie = { 
        movie_id,
        poster,
        original_title,
        english_title,
        is_gruvi
      };
      resolve(update_movie);

    }).catch(err => reject(err));
  });
}


const attributeMovieToEntry = (dynamodb, entry_id, movie_id) => {
  return new Promise( (resolve,reject) => {
    const params = {
      ExpressionAttributeValues: {
        ":mid": {
          S: movie_id
        }, 
      }, 
      Key: {
        "MovieEntryID": {
          S: entry_id
        } 
      }, 
      TableName: "MovieEntry", 
      UpdateExpression: `SET MovieID = :mid`
    };


    updateItemDynamodb(dynamodb, params).then( data => {
      resolve();
    }).catch(err => reject(err));
  });
}


const deleteItem = (dynamodb, entry_id, msg) => {
  return new Promise( (resolve,reject) => {
    const params = {
      ExpressionAttributeValues: {
        ":d": {
          BOOL: true
        }, 
      }, 
      Key: {
        "MovieEntryID": {
           S: entry_id
        }, 
      }, 
      TableName: "MovieEntry",
      UpdateExpression: "SET Archived = :d"
    };
    updateItemDynamodb(dynamodb, params).then( data => {
      resolve();
    }).catch(err => reject(err));

  });
}

export default {
  getAdminMoviesInfo,
  getMovieList,
  getGruviList,
  getDataForMovie,
  createMovie,
  createMovieEntry,
  updateMovieEntry,
  updateMovie,
  attributeMovieToEntry,
  deleteItem
}



// WEBPACK FOOTER //
// ./src/lib/movie_api.js